/**
 * Skill Execution Analytics Component
 * Displays statistics and charts for skill matching performance
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getSkillExecutionStats,
  getSkillExecutions,
  exportSkillExecutions,
  type SkillExecutionStats,
  type SkillExecutionRecord,
} from "@/lib/api/classificationAnalytics";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Download,
  RefreshCw,
  TrendingUp,
  Target,
  Layers,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export function SkillExecutionAnalytics() {
  const { messages } = useI18n();
  const t = messages.classificationAnalytics;
  const tCommon = messages.common;

  const [timeRange, setTimeRange] = useState<number>(24);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch stats
  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery<SkillExecutionStats>({
    queryKey: ["skill-execution-stats", timeRange],
    queryFn: () => getSkillExecutionStats({ hours: timeRange }),
  });

  // Fetch recent executions
  const { data: executions } = useQuery<{
    total: number;
    records: SkillExecutionRecord[];
  }>({
    queryKey: ["skill-executions", timeRange],
    queryFn: () => getSkillExecutions({ limit: 10 }),
  });

  const handleExport = async (format: "csv" | "json") => {
    setExportLoading(true);
    try {
      await exportSkillExecutions(format, { limit: 1000 });
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export: ${error}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Prepare chart data
  const matchLayerData = stats
    ? Object.entries(stats.by_match_layer).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        count: value,
      }))
    : [];

  const skillData = stats
    ? Object.entries(stats.by_skill)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, count]) => ({
          name,
          count,
          confidence: (stats.avg_confidence_by_skill[name] || 0) * 100,
        }))
    : [];

  const sourceData = stats
    ? Object.entries(stats.by_source).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
    : [];

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t.error}</p>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          {tCommon.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={timeRange.toString()}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24">{t.timeRange.last24h}</option>
            <option value="168">{t.timeRange.last7d}</option>
            <option value="720">{t.timeRange.last30d}</option>
          </select>

          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t.refresh}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("csv")}
            disabled={exportLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            {t.export.csv}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("json")}
            disabled={exportLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            {t.export.json}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.skillExecution.stats.totalExecutions}
            </span>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading ? "..." : stats?.total_executions.toLocaleString()}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.skillExecution.stats.activeSkills}
            </span>
            <Target className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading ? "..." : Object.keys(stats?.by_skill || {}).length}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.skillExecution.stats.autoMatchRate}
            </span>
            <Layers className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats
              ? Math.round(
                  ((stats.by_source.auto || 0) / stats.total_executions) * 100
                ) + "%"
              : "0%"}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">
              {t.skillExecution.stats.ruleMatchRate}
            </span>
            <Users className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {isLoading
              ? "..."
              : stats
              ? Math.round(
                  ((stats.by_match_layer.rule || 0) / stats.total_executions) *
                    100
                ) + "%"
              : "0%"}
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Match Layer Distribution */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.skillExecution.charts.matchLayer}
            </h3>
          </div>
          {matchLayerData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={matchLayerData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {matchLayerData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.skillExecution.noData}
            </div>
          )}
        </Card>

        {/* Source Distribution */}
        <Card className="bg-white p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t.skillExecution.charts.matchSource}
            </h3>
          </div>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sourceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sourceData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t.skillExecution.noData}
            </div>
          )}
        </Card>
      </div>

      {/* Top Skills */}
      <Card className="bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t.skillExecution.charts.topSkills}
          </h3>
        </div>
        {skillData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={skillData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="count"
                fill="#8884d8"
                name={t.skillExecution.charts.topSkills}
              />
              <Bar
                yAxisId="right"
                dataKey="confidence"
                fill="#82ca9d"
                name={`${t.skillExecution.confidence} (%)`}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[400px] flex items-center justify-center text-gray-400">
            {t.skillExecution.noData}
          </div>
        )}
      </Card>

      {/* Recent Executions */}
      <Card className="bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t.skillExecution.recentExecutions}
          </h3>
        </div>
        <div className="space-y-4">
          {executions?.records.map((record) => (
            <div
              key={record.id}
              className="flex items-start justify-between border-b border-gray-200 pb-3 last:border-0"
            >
              <div className="space-y-1 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {record.skill_id}
                </p>
                <p className="text-sm text-gray-600">{record.user_query}</p>
                <div className="flex gap-2 text-xs text-gray-500">
                  <span>
                    {t.skillExecution.matchSource}: {record.match_source}
                  </span>
                  {record.matched_rule && <span>• {record.matched_rule}</span>}
                  {record.confidence && (
                    <span>
                      • {t.skillExecution.confidence}:{" "}
                      {(record.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                {new Date(record.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
